import { useEffect, useMemo, useState } from 'react'

import { useTranslation } from 'react-i18n-lite'

import {
  FilterQuery,
  SkeletonDots,
  YesObject
} from '@yes.technology/react-toolkit'

import ContactObjectCard from 'component/ContactObjectCard'
import GenericObjectCard from 'component/GenericObjectCard'
import TaskObjectCard from 'component/TaskObjectCard'

import { useObjectclass } from 'objectclass/shared'

import useStyleMaps from 'site/Renderer/hooks/useStyleMap'

import { useSiteState } from 'siteState/shared'

import { useFetchYesObjects } from 'yesObject/shared'

import Pagination from '../Pagination'
import SectionDivider from '../SectionDivider'

import CardContainer from './CardContainer'
import ClassifiedList from './ClassifiedList'

import { DataCardsProps } from './DataCards.types'

import useMergedFilterQuery from 'site/shared/hooks/useMergedFilterQuery'
import useClassification from './hooks/useClassification'

const validCardModels = {
  GenericObjectCard,
  TaskObjectCard,
  ContactObjectCard,
  CollapsibleObjectCard: GenericObjectCard
}

const FETCH_LIMIT = 10000

const DataCards = ({ props }: DataCardsProps) => {
  const {
    uuid_objectclass: objectclassUuid = '',
    uuid_filteraction: filteractionUuid,
    uuid_classification: classificationUuid,
    card_model: cardModel,
    preview_mode: previewMode = false,
    show_pagination: showPagination = true,
    pagination_limit: paginationLimit = 12,
    card_props: cardProps,
    styleMap,
    rowStyleMap,
    button_props: buttonProps,
    extra_card_props: extraCardProps,
    render_without_wrapper: renderWithoutWrapper = false,
    card_height: cardHeight = 'fit-content',
    objects,
    merge_filter_query: manifestFilterQuery,
    return_on_finished_interaction: returnOnFinishedInteraction = false
  } = props

  const { t } = useTranslation()

  const CardComponent = validCardModels[cardModel]

  const {
    classification,
    isLoading: isLoadingClassification,
    childrensQuery
  } = useClassification({
    classificationUuid
  })

  const { objectclass, isLoading: isLoadingObjectClass } = useObjectclass({
    objectclassUuid
  })

  const [filterQuery] = useSiteState<FilterQuery>('filter-query')

  const finalFilterQuery = useMergedFilterQuery({
    filterQuery,
    manifestFilterQuery
  }) as FilterQuery

  const styleProps = useStyleMaps(styleMap)

  const rowStyleProps = useStyleMaps(rowStyleMap)

  const shouldRenderClassifiedList =
    classificationUuid && cardModel === 'CollapsibleObjectCard'

  const [currentSimulatedPage, setCurrentSimulatedPage] = useState(1)

  const {
    yesObjects,
    isLoading: isLoadingObjects,
    fetchYesObjects
  } = useFetchYesObjects({
    initialPagination: { limit: FETCH_LIMIT, offset: 0 },
    filterBody: {
      query: childrensQuery
        ? { $and: [childrensQuery, finalFilterQuery] }
        : finalFilterQuery,
      objectclass: objectclassUuid
    },
    filteractionUuid,
    shouldMergeFilters: !!manifestFilterQuery,
    enabled: false
  })

  const finalObjects = objects || yesObjects

  const paginatedObjects = useMemo(() => {
    if (!showPagination) {
      return finalObjects
    }
    const startIndex = (currentSimulatedPage - 1) * paginationLimit
    const endIndex = startIndex + paginationLimit
    return finalObjects.slice(startIndex, endIndex)
  }, [finalObjects, currentSimulatedPage, paginationLimit, showPagination])

  const simulatedPagination = useMemo(() => {
    const totalItems = finalObjects.length
    const totalPages = Math.ceil(totalItems / paginationLimit)
    const hasNext = currentSimulatedPage < totalPages
    const hasPrevious = currentSimulatedPage > 1

    return {
      limit: paginationLimit,
      offset: (currentSimulatedPage - 1) * paginationLimit,
      total: totalItems,
      totalPages,
      hasNext,
      hasPrevious
    }
  }, [finalObjects.length, paginationLimit, currentSimulatedPage])

  useEffect(() => {
    if (finalFilterQuery || filteractionUuid) {
      fetchYesObjects()
      setCurrentSimulatedPage(1)
    }
  }, [finalFilterQuery, filteractionUuid, fetchYesObjects])

  const isLoading =
    isLoadingObjects || isLoadingObjectClass || isLoadingClassification

  const renderCardList = () => {
    if (isLoading && finalObjects?.length === 0) {
      return null
    }

    if (shouldRenderClassifiedList) {
      return (
        <ClassifiedList
          classifications={classification}
          objects={paginatedObjects}
          cardProps={cardProps}
          cardModel={cardModel}
        />
      )
    }

    return (
      <div className='row' style={rowStyleProps}>
        {paginatedObjects?.map((object: YesObject) => {
          return (
            <CardContainer
              key={object.uuid}
              cardType={cardModel}
              previewMode={previewMode}
              cardHeight={cardHeight}
            >
              <CardComponent
                {...object}
                buttonProps={buttonProps}
                {...extraCardProps}
                returnOnFinish={returnOnFinishedInteraction}
              />
            </CardContainer>
          )
        })}
      </div>
    )
  }

  const paginationElement = showPagination && (
    <Pagination
      pagination={simulatedPagination}
      setCurrentPage={setCurrentSimulatedPage}
      sectionText={t('data-cards.section')}
      arrowOrientation='horizontalArrows'
      showTotalPages
    />
  )

  const shouldDisableSection =
    finalObjects?.length === 0 && !filterQuery && !filteractionUuid

  if (previewMode) {
    return (
      <div style={styleProps}>
        {isLoading && (
          <div className='mt-3 d-flex justify-content-center'>
            <SkeletonDots />
          </div>
        )}

        {renderCardList()}
      </div>
    )
  }

  if (renderWithoutWrapper) {
    return (
      <>
        {isLoading && (
          <div className='mt-3 d-flex justify-content-center'>
            <SkeletonDots />
          </div>
        )}

        {renderCardList()}
      </>
    )
  }

  return (
    <SectionDivider
      title={objectclass?.des || ''}
      centerHeaderElement={paginationElement || undefined}
      backgroundColor='#004099'
      disabled={shouldDisableSection}
      style={styleProps}
    >
      <div>
        {isLoading && (
          <div className='mt-3 d-flex justify-content-center'>
            <SkeletonDots />
          </div>
        )}

        {renderCardList()}
      </div>
    </SectionDivider>
  )
}

export default DataCards
